import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { forgetPushDevice } from '../../utils/push';

// Phone: "Delete my account" confirmation (Google Play requires an in-app way to delete an account).
export const DeleteAccountSheet = ({ isOwner, onClose }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ready = text.trim().toUpperCase() === 'DELETE';

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('delete-account', { body: { confirm: 'DELETE' } });
      if (fnErr) throw new Error(fnErr.message || 'Could not reach the server');
      if (!data?.ok) { setError(data?.message || 'Could not delete the account.'); setBusy(false); return; }
      await forgetPushDevice();
      try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* the login is already gone */ }
      toast.success('Your account has been deleted');
      onClose();
      navigate('/login', { replace: true });
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return createPortal(
    <div className="m-sheet-scrim" onClick={busy ? undefined : onClose}>
      <div className="m-sheet m-delete" onClick={e => e.stopPropagation()}>
        <div className="m-sheet-grip" />
        <div className="m-sheet-head">
          <div className="m-sheet-title">Delete my account</div>
          <button className="m-iconbtn" aria-label="Close" onClick={onClose} disabled={busy}><X size={22} /></button>
        </div>

        <div className="m-delete-warn">
          <AlertTriangle size={20} />
          <span>This cannot be undone.</span>
        </div>

        {isOwner ? (
          <p className="m-delete-text">
            Everything in your account will be permanently deleted: all your vehicles, records, photos, documents and files,
            and your team's access to them. Your subscription will be cancelled. Billing records are kept only as long as the
            law requires. People on your team keep their own logins, but lose access to your fleet.
          </p>
        ) : (
          <p className="m-delete-text">
            Your login and your access to this fleet will be permanently deleted. Records you entered for the fleet stay with
            the fleet owner. The owner's account is not affected.
          </p>
        )}

        <label className="m-delete-label" htmlFor="m-delete-input">Type DELETE to confirm</label>
        <input
          id="m-delete-input"
          className="m-delete-input"
          value={text}
          onChange={e => setText(e.target.value)}
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="DELETE"
          disabled={busy}
        />

        {error && <div className="m-delete-error">{error}</div>}

        <button className="m-delete-go" disabled={!ready || busy} onClick={submit}>
          {busy ? 'Deleting… please wait' : 'Permanently delete my account'}
        </button>
        <button className="m-delete-cancel" disabled={busy} onClick={onClose}>Keep my account</button>
      </div>
    </div>,
    document.body
  );
};
